import React, { useRef, useState } from "react";
import styled from "styled-components";
import { File as FileIcon, Info } from "react-feather";
import { Input } from "@cinesys/ui/components";

export const Page = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  flex: 1 0 auto;
  padding: 20px 0 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
`;

export const Lead = styled.p`
  margin: 5px 0 0;
  color: var(--abLabel);
  font-size: 14px;
  line-height: 1.5;
`;

export const Card = styled.section`
  background: var(--dark090);
  border: 1px solid var(--abField);
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

export const CardTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 20px;
  align-items: start;

  @media (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Full = styled.div`
  grid-column: 1 / -1;
`;

const HeaderBar = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const HeaderActions = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

/** Page title and lead; secondary actions sit on its right. Submit actions go in the shell footer. */
export const PageHeader = ({
  title,
  lead,
  actions,
}: {
  title: string;
  lead?: React.ReactNode;
  actions?: React.ReactNode;
}) => (
  <HeaderBar>
    <div>
      <PageTitle>{title}</PageTitle>
      {lead && <Lead>{lead}</Lead>}
    </div>
    {actions && <HeaderActions>{actions}</HeaderActions>}
  </HeaderBar>
);

export const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

export const Muted = styled.span`
  color: var(--abLabel);
  font-size: 13px;
`;

export const Mono = styled.code`
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 13px;
`;

const FileNameBox = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 6px 10px;
  border: 1px solid var(--abField);
  border-radius: 6px;
  background: var(--dark100);

  svg {
    flex: 0 0 auto;
    color: var(--abLabel);
  }

  code {
    min-width: 0;
    font-size: 14px;
    overflow-wrap: anywhere;
  }
`;

/** A file name, marked with a file icon so it reads as a name and not as free text. */
export const FileName = ({
  children,
  testId,
}: {
  children: string;
  testId?: string;
}) => (
  <FileNameBox data-testid={testId}>
    <FileIcon size={16} aria-hidden />
    <Mono>{children}</Mono>
  </FileNameBox>
);

export const FieldNote = styled.div`
  margin-top: 5px;
  font-size: 13px;
  line-height: 1.4;
  color: var(--abLabel);
`;

/** An error under a kit input, which already leaves its own bottom margin to reclaim. */
export const FieldError = styled.div`
  margin: -20px 0 15px;
  font-size: 13px;
  line-height: 1.4;
  color: var(--abDanger);
`;

export const ReadOnly = ({
  label,
  children,
}: {
  label: string;
  children?: string;
}) => (
  <div>
    <Input
      label={label}
      value={children ?? ""}
      onChange={() => undefined}
      disabled
    />
  </div>
);

export const Chip = styled.span<{ $tone: "ok" | "warn" | "bad" | "info" }>`
  display: inline-block;
  padding: 3px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(p) => (p.$tone === "ok" ? "#fff" : "#111")};
  background: ${(p) =>
    p.$tone === "ok"
      ? "var(--abOk)"
      : p.$tone === "warn"
      ? "var(--abWarn)"
      : p.$tone === "bad"
      ? "var(--abDanger)"
      : "var(--abLabel)"};
`;

const BarOuter = styled.div`
  height: 8px;
  border-radius: 4px;
  background: var(--abField);
  overflow: hidden;
  flex: 1 1 120px;
`;
const BarInner = styled.div<{ $pct: number; $bad?: boolean }>`
  height: 100%;
  width: ${(p) => p.$pct}%;
  background: ${(p) => (p.$bad ? "var(--abDanger)" : "var(--abOk)")};
  transition: width 0.2s ease;
`;

export const ProgressBar = ({
  percent,
  bad,
  label,
}: {
  percent: number;
  bad?: boolean;
  label: string;
}) => (
  <BarOuter
    role="progressbar"
    aria-label={label}
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={Math.round(percent)}
    data-bad={bad ? "true" : undefined}
  >
    <BarInner $pct={Math.max(0, Math.min(100, percent))} $bad={bad} />
  </BarOuter>
);

const Zone = styled.label<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 30px 20px;
  text-align: center;
  cursor: pointer;
  border: 2px dashed
    ${(p) => (p.$active ? "var(--abAccent)" : "var(--abField)")};
  border-radius: 8px;
  background: var(--dark090);

  &:focus-within {
    border-color: var(--abAccent);
  }

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
  }
`;

export const DropZone = ({
  multiple,
  accept,
  title,
  hint,
  onFiles,
  testId,
}: {
  multiple?: boolean;
  accept?: string;
  title: string;
  hint?: string;
  onFiles: (files: File[]) => void;
  testId?: string;
}) => {
  const [active, setActive] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  return (
    <Zone
      $active={active}
      onDragOver={(e) => {
        e.preventDefault();
        setActive(true);
      }}
      onDragLeave={() => setActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setActive(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length) {
          onFiles(multiple ? files : files.slice(0, 1));
        }
      }}
    >
      <strong>{title}</strong>
      {hint && <Muted>{hint}</Muted>}
      <input
        ref={input}
        type="file"
        multiple={multiple}
        accept={accept}
        data-testid={testId}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) {
            onFiles(files);
          }
          e.target.value = "";
        }}
      />
    </Zone>
  );
};

/** Fills the space left of a footer's submit button so status text never pushes the button. */
export const FooterStatus = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const InfoNoteRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 5px;
  font-size: 14px;
  line-height: 20px;
  color: var(--gray100);

  svg {
    flex: 0 0 auto;
    margin-top: 1.5px;
  }

  span {
    min-width: 0;
    overflow-wrap: anywhere;
    text-wrap: pretty;
  }
`;

/** Quiet inline notice: an info icon beside plain text. */
export const InfoNote = ({ children }: { children: React.ReactNode }) => (
  <InfoNoteRow>
    <Info size={17} aria-hidden />
    <span>{children}</span>
  </InfoNoteRow>
);

const BlockBox = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
  border: 1px solid var(--abField);
  border-radius: 8px;
  background: var(--dark090);
`;

const BlockHeading = styled.h3`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--abLabel);
`;

/** A titled, bordered group inside a dialog so each kind of information reads as its own section. */
export const Block = ({
  title,
  children,
  ...rest
}: {
  title: string;
  children: React.ReactNode;
} & React.HTMLAttributes<HTMLElement>) => (
  <BlockBox {...rest}>
    <BlockHeading>{title}</BlockHeading>
    {children}
  </BlockBox>
);
